import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Trash, PencilSimple, Check, X, Sparkle, ShieldCheck, CircleNotch, ArrowsClockwise, SealCheck, Clock, WarningCircle, CheckCircle, FilePdf, Briefcase, Airplane, Lightning,
} from "@phosphor-icons/react";
import api, { downloadFile, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, longDate, dmy } from "@/lib/format";
import { Card, Loading, catColor } from "@/components/riq";
import { sourceMeta } from "@/components/SourceBadge";
import { MerchantLogo } from "@/components/riq";
import { WARRANTY_PERIODS, periodLabel } from "@/pages/Warranties";
import { ReceiptAttachments } from "@/components/ReceiptAttachments";
import { BasketBreakdown } from "@/components/CheapestBasket";

const CATEGORIES = ["Groceries", "Household", "Restaurants", "Fuel", "Clothing", "Electronics", "Subscriptions", "Insurance", "Telecommunications", "Medical Aid", "Loans", "Home Loan", "Car Installment", "Rates", "Garden Services", "Pool Services", "Domestic Helper", "Health & Beauty", "Entertainment", "Transport", "Other"];

export default function ReceiptDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const cur = user?.currency || "ZAR";
  const [rec, setRec] = useState(null);
  const [edit, setEdit] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [rescanning, setRescanning] = useState(false);
  const [cats, setCats] = useState(CATEGORIES);

  useEffect(() => {
    api.get(`/receipts/${id}`).then((r) => setRec(r.data)).catch(() => setRec(false));
  }, [id]);

  useEffect(() => {
    api.get("/settings/categories").then((r) => setCats(r.data.categories || CATEGORIES)).catch(() => {});
  }, []);

  if (rec === null) return <Loading />;
  if (rec === false) return <div className="text-muted-foreground">Receipt not found.</div>;

  const startEdit = () => { setDraft(JSON.parse(JSON.stringify(rec))); setEdit(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        merchant_name: draft.merchant_name, merchant_category: draft.merchant_category,
        branch: draft.branch, location: draft.location, date: draft.date, time: draft.time,
        currency: draft.currency || "ZAR", subtotal: draft.subtotal, discount: draft.discount || 0,
        tax: draft.tax, tip: draft.tip != null && draft.tip !== "" ? Number(draft.tip) : null, total: Number(draft.total), payment_method: draft.payment_method,
        reference_number: draft.reference_number, storage_paths: rec.storage_paths || [],
        ocr_text: rec.ocr_text, confidence: rec.confidence,
        items: draft.items.map((it) => ({
          description: it.description || it.canonical_name, canonical_name: it.canonical_name,
          brand: it.brand, size: it.size, unit: it.unit, category: it.category || draft.merchant_category,
          quantity: Number(it.quantity) || 1, unit_price: it.unit_price != null ? Number(it.unit_price) : null,
          total_price: it.total_price != null ? Number(it.total_price) : null,
          confidence: it.confidence != null ? Number(it.confidence) : null,
        })),
      };
      const { data } = await api.patch(`/receipts/${id}`, payload);
      const detail = await api.get(`/receipts/${id}`);
      setRec(detail.data); setEdit(false);
      toast.success("Receipt updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save");
    } finally { setSaving(false); }
  };

  const del = async () => {
    if (!window.confirm("Delete this receipt? This cannot be undone.")) return;
    try { await api.delete(`/receipts/${id}`); toast.success("Receipt deleted"); navigate("/receipts"); }
    catch { toast.error("Could not delete"); }
  };

  const regenerate = async () => {
    setRegenerating(true);
    try {
      await api.post(`/receipts/${id}/regenerate`);
      const detail = await api.get(`/receipts/${id}`);
      setRec(detail.data);
      toast.success("Re-generated from the original receipt");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not re-generate");
    } finally { setRegenerating(false); }
  };

  const refresh = async () => { const detail = await api.get(`/receipts/${id}`); setRec(detail.data); };

  const confirmDup = async () => {
    try {
      await api.post(`/receipts/${id}/confirm-duplicate`);
      await refresh();
      toast.success("Approved — this receipt now counts towards your totals");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not approve");
    }
  };
  const rescanTotals = async () => {
    setRescanning(true);
    try {
      const { data } = await api.post(`/receipts/${id}/regenerate-totals`);
      setRec(data);
      toast.success("Re-read the total & date from the slip");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not re-run AI");
    } finally { setRescanning(false); }
  };
  const addWarranty = async (itemId, months) => {
    try { await api.post("/warranties", { receipt_id: id, item_id: itemId || undefined, period_months: months }); await refresh(); toast.success("Warranty added"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not add warranty"); }
  };
  const changeWarrantyPeriod = async (wid, months) => {
    try { await api.patch(`/warranties/${wid}`, { period_months: months }); await refresh(); toast.success("Warranty period updated"); }
    catch { toast.error("Could not update"); }
  };
  const removeWarranty = async (wid) => {
    if (!window.confirm("Remove this warranty?")) return;
    try { await api.delete(`/warranties/${wid}`); await refresh(); toast.success("Warranty removed"); }
    catch { toast.error("Could not remove"); }
  };

  const view = edit ? draft : rec;
  const imgPath = rec.storage_paths?.[0];
  const conf = rec.confidence || {};
  const lowConfidence = !!imgPath && Object.values(conf).some((v) => typeof v === "number" && v < 100);

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-neutral-500 font-medium"><ArrowLeft size={18} /> Back</button>
        <div className="flex items-center gap-2">
          {!edit ? (
            <>
              <button data-testid="edit-receipt-btn" onClick={startEdit} className="flex items-center gap-1.5 rounded-full border border-riq-border px-4 py-2 text-sm hover:border-riq-primary"><PencilSimple size={16} /> Edit</button>
              <button data-testid="rescan-totals-btn" onClick={rescanTotals} disabled={rescanning} className="flex items-center gap-1.5 rounded-full border border-riq-border px-4 py-2 text-sm hover:border-riq-primary disabled:opacity-60">{rescanning ? <CircleNotch size={16} className="animate-spin" /> : <ArrowsClockwise size={16} />} Re-run total & date</button>
              <button data-testid="delete-receipt-btn" onClick={del} className="flex items-center gap-1.5 rounded-full border border-riq-border px-4 py-2 text-sm text-destructive hover:border-destructive"><Trash size={16} /> Delete</button>
            </>
          ) : (
            <>
              <button onClick={() => setEdit(false)} className="flex items-center gap-1.5 rounded-full border border-riq-border px-4 py-2 text-sm"><X size={16} /> Cancel</button>
              <button data-testid="save-edit-btn" disabled={saving} onClick={save} className="flex items-center gap-1.5 rounded-full bg-riq-primary text-white px-4 py-2 text-sm disabled:opacity-60">{saving ? <CircleNotch size={16} className="animate-spin" /> : <Check size={16} />} Save</button>
            </>
          )}
        </div>
      </div>

      {/* Header */}
      <Card className="bg-riq-primary text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07] riq-noise" />
        <div className="relative">
          {edit ? (
            <div className="grid grid-cols-2 gap-3">
              <EField label="Merchant" value={draft.merchant_name} onChange={(v) => setDraft({ ...draft, merchant_name: v })} testId="edit-merchant" />
              <ESelect label="Category" value={draft.merchant_category} onChange={(v) => setDraft({ ...draft, merchant_category: v })} options={cats} testId="edit-category" />
              <EField label="Date" type="date" value={draft.date} onChange={(v) => setDraft({ ...draft, date: v })} testId="edit-date" />
              <EField label="Total" type="number" value={draft.total} onChange={(v) => setDraft({ ...draft, total: v })} testId="edit-total" />
              <EField label="Tip / Gratuity" type="number" value={draft.tip ?? ""} onChange={(v) => setDraft({ ...draft, tip: v })} testId="edit-tip" />
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/60">{view.merchant_category}</div>
                  <h1 data-testid="receipt-merchant-name" className="font-head text-3xl font-bold tracking-tight mt-1">{view.merchant_name}</h1>
                  <div className="text-white/70 text-sm mt-1">{longDate(view.date)}{view.time ? ` · ${view.time}` : ""}{view.branch ? ` · ${view.branch}` : ""}</div>
                  {(() => { const m = sourceMeta(rec.source); return (
                    <span data-testid={`detail-source-${rec.source || "upload"}`} className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-1 text-xs font-semibold text-white">
                      <m.Icon size={13} weight="bold" /> {m.label}
                    </span>
                  ); })()}
                  {(rec.ocr_engine === "gemini" || rec.ocr_engine === "emergent" || rec.ocr_engine === "tesseract") && (
                    <span
                      data-testid="ocr-fallback-badge"
                      title="Your primary scanner was unavailable, so this receipt was read by a backup engine. Please double-check the details."
                      className="mt-2 ml-2 inline-flex items-center gap-1.5 rounded-full bg-riq-gold/25 px-2.5 py-1 text-xs font-semibold text-white ring-1 ring-white/25"
                    >
                      <Lightning size={13} weight="fill" className="text-riq-gold" />
                      {rec.ocr_engine === "gemini" ? "Scanned via Gemini backup" : rec.ocr_engine === "emergent" ? "Scanned via Emergent backup" : "Scanned via free OCR backup"}
                    </span>
                  )}
                </div>
                <span><MerchantLogo name={view.merchant_name} size={52} /></span>
              </div>
              <div data-testid="receipt-total" className="font-head text-5xl font-bold mt-6">{money(view.total, cur)}</div>
              {rec.is_foreign && (
                <div data-testid="receipt-foreign" className="mt-2 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-sm">
                  Paid in {money(rec.original_total, rec.original_currency)} {rec.original_currency} · 1 {rec.original_currency} = {rec.exchange_rate} {cur}
                  {rec.fx_converted === false && " (approx.)"}
                </div>
              )}
              {rec.merchant_visits_this_month > 1 && (
                <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-sm"><Sparkle size={14} weight="fill" className="text-riq-gold" /> This is purchase #{rec.merchant_visits_this_month} from {view.merchant_name} this month.</div>
              )}
            </>
          )}
        </div>
      </Card>

      {/* Trip / expense: assign to a trip (+ description for business) */}
      {!edit && (
        <BusinessExpenseCard rec={rec} onSaved={refresh} isBiz={user?.account_type === "business"} />
      )}

      {/* Review status banner */}
      {!edit && rec.review_status && rec.review_status !== "approved" && (
        <Card data-testid="review-banner" className={rec.review_status === "rejected" ? "border-2 border-[#f3b1ab] bg-[#fff6f5]" : "border-2 border-riq-gold/50 bg-riq-gold/10"}>
          <div className="flex items-start gap-3">
            <div className="shrink-0 mt-0.5">{rec.review_status === "rejected" ? <WarningCircle size={22} weight="fill" className="text-[#b3261e]" /> : <Clock size={22} weight="fill" className="text-[#8a5a00]" />}</div>
            <div className="flex-1 min-w-0">
              <div className="font-head font-semibold text-riq-primary">{rec.review_code === "duplicate" ? "Possible duplicate — not counted yet" : rec.review_status === "rejected" ? "This receipt was rejected" : rec.review_status === "needs_date_fix" ? "Please confirm the purchase date" : "Please double-check the total"}</div>
              <p className="text-sm text-neutral-600 mt-1">{rec.review_reason}</p>
              {rec.duplicate_of && <button data-testid="review-view-original" onClick={() => navigate(`/receipts/${rec.duplicate_of}`)} className="text-sm text-riq-primary font-medium mt-1">View the original receipt →</button>}
              <div className="mt-3 flex flex-wrap gap-2">
                {rec.review_code === "duplicate" && <button data-testid="review-approve-duplicate" onClick={confirmDup} className="rounded-full bg-riq-primary text-white px-4 py-2 text-sm font-medium flex items-center gap-1.5"><CheckCircle size={15} weight="fill" /> It's a separate purchase — count it</button>}
                {(rec.review_code === "stale_date" || rec.review_code === "stale_uncorrected") && <button data-testid="review-fix-date" onClick={startEdit} className="rounded-full bg-riq-primary text-white px-4 py-2 text-sm font-medium flex items-center gap-1.5"><PencilSimple size={15} /> Correct date & re-submit</button>}
                {rec.review_code !== "duplicate" && <button data-testid="review-rescan" onClick={rescanTotals} disabled={rescanning} className="rounded-full border border-riq-primary text-riq-primary px-4 py-2 text-sm font-medium flex items-center gap-1.5 disabled:opacity-60">{rescanning ? <CircleNotch size={15} className="animate-spin" /> : <ArrowsClockwise size={15} />} Re-run AI (total & date)</button>}
                {rec.review_code !== "duplicate" && <button data-testid="review-edit" onClick={startEdit} className="rounded-full border border-riq-border px-4 py-2 text-sm hover:border-riq-primary flex items-center gap-1.5"><PencilSimple size={15} /> Edit</button>}
                <button data-testid="review-delete" onClick={del} className="rounded-full border border-riq-border px-4 py-2 text-sm text-destructive hover:border-destructive flex items-center gap-1.5"><Trash size={15} /> Delete</button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Items */}
      <Card>
        <div className="flex items-center justify-between mb-4"><h3 className="font-head font-semibold text-riq-primary">Items ({view.items?.length || 0})</h3>{conf.items != null && <ConfBadge label="Items" value={conf.items} />}</div>
        {edit ? (
          <div className="space-y-2">
            {draft.items.map((it, i) => (
              <div key={i} className="rounded-xl border border-riq-border p-3 bg-secondary/40">
                <div className="flex items-center gap-2">
                  <input value={it.canonical_name || ""} onChange={(e) => { const items = [...draft.items]; items[i] = { ...it, canonical_name: e.target.value }; setDraft({ ...draft, items }); }} className="flex-1 bg-white rounded-lg border border-riq-border px-3 py-2 text-sm" data-testid={`edit-item-name-${i}`} />
                  <button onClick={() => setDraft({ ...draft, items: draft.items.filter((_, j) => j !== i) })} className="text-neutral-400 hover:text-destructive"><Trash size={16} /></button>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {["quantity", "unit_price", "total_price"].map((k, ki) => (
                    <input key={k} type="number" value={it[k] ?? ""} placeholder={["Qty", "Unit", "Total"][ki]} onChange={(e) => { const items = [...draft.items]; items[i] = { ...it, [k]: e.target.value }; setDraft({ ...draft, items }); }} className="bg-white rounded-lg border border-riq-border px-2 py-1.5 text-sm" />
                  ))}
                </div>
              </div>
            ))}
            <button onClick={() => setDraft({ ...draft, items: [...draft.items, { canonical_name: "", quantity: 1, unit_price: 0, total_price: 0, category: draft.merchant_category }] })} className="text-riq-primary text-sm">+ Add item</button>
          </div>
        ) : (view.items?.length ? (
          <div className="divide-y divide-riq-border -mx-2">
            <div className="flex items-center px-2 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"><span className="flex-1">Product</span><span className="w-12 text-right">Qty</span><span className="w-24 text-right">Price</span></div>
            {view.items.map((it, i) => (
              <div key={i} data-testid={`detail-item-${i}`} className="flex items-center px-2 py-3 text-sm">
                <span className="flex-1 flex items-center gap-2 min-w-0">
                  <span className="font-medium truncate">{it.canonical_name || it.description}</span>
                  {it.size && <span className="text-muted-foreground text-xs">{it.size}</span>}
                  {it.confidence != null && <ItemConf pct={it.confidence} />}
                </span>
                <span className="w-12 text-right text-muted-foreground">{it.quantity}</span>
                <span className="w-24 text-right font-medium">{money(it.total_price ?? it.unit_price, cur)}</span>
              </div>
            ))}
          </div>
        ) : <div className="text-sm text-muted-foreground">No items extracted.</div>)}
      </Card>

      {/* Breakdown */}
      <Card>
        <h3 className="font-head font-semibold text-riq-primary mb-4">Breakdown</h3>
        <div className="space-y-2 text-sm">
          <Row label="Subtotal" value={money(view.subtotal, cur)} />
          {view.discount ? <Row label="Discount" value={`-${money(view.discount, cur)}`} /> : null}
          <Row label="VAT / Tax" value={money(view.tax, cur)} />
          {view.tip ? <Row label="Tip / Gratuity" value={money(view.tip, cur)} /> : null}
          <div className="border-t border-riq-border pt-2 mt-2"><Row label="Total" value={money(view.total, cur)} bold /></div>
          {view.payment_method && <Row label="Payment" value={view.payment_method} />}
          {view.reference_number && <Row label="Reference" value={view.reference_number} />}
          <div className="border-t border-riq-border pt-2 mt-2 space-y-1">
            <Row label="Receipt date" value={dmy(view.date)} />
            {rec.created_at && <Row label="Scanned on" value={dmy(rec.created_at)} />}
          </div>
        </div>
      </Card>

      {/* Warranty */}
      {!edit && (
        <WarrantyBlock warranties={rec.warranties || []} items={rec.items || []} onAdd={addWarranty} onChange={changeWarrantyPeriod} onRemove={removeWarranty} />
      )}

      {/* AI extraction confidence */}
      {Object.keys(conf).length > 0 && (
        <Card>
          <div className="flex items-center gap-2 mb-3"><ShieldCheck size={18} weight="duotone" className="text-riq-primary" /><h3 className="font-head font-semibold text-riq-primary">AI Extraction</h3></div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(conf).map(([k, v]) => <ConfBadge key={k} label={k} value={v} />)}
          </div>
          {lowConfidence && !edit && (
            <div className="mt-4 rounded-xl bg-[#FFF7EA] border border-[#F5D9A8] p-4">
              <p className="text-sm text-[#8a5a00] leading-snug mb-3">
                Some fields weren't read with full confidence. You can re-run the AI on the original receipt to try for a better result.
              </p>
              <button
                data-testid="regenerate-receipt-btn"
                onClick={regenerate} disabled={regenerating}
                className="inline-flex items-center gap-2 rounded-full bg-riq-primary text-white px-5 py-2.5 text-sm font-medium disabled:opacity-60 hover:-translate-y-0.5 transition-transform"
              >
                {regenerating ? <CircleNotch size={16} className="animate-spin" weight="bold" /> : <ArrowsClockwise size={16} weight="bold" />}
                {regenerating ? "Re-generating…" : "Re-generate from original"}
              </button>
            </div>
          )}
        </Card>
      )}

      <ReceiptAttachments paths={rec.storage_paths} />
      <BasketBreakdown receiptId={rec.id} />
    </div>
  );
}

function BusinessExpenseCard({ rec, onSaved, isBiz }) {
  const [trips, setTrips] = useState([]);
  const [desc, setDesc] = useState(rec.description || "");
  const [tripId, setTripId] = useState(rec.trip_id || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/trips").then((r) => setTrips(r.data.trips)).catch(() => {}); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/receipts/${rec.id}/meta`, { description: desc, trip_id: tripId });
      toast.success(isBiz ? "Expense details saved" : "Trip details saved");
      onSaved && onSaved();
    } catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  // Auto-suggest: a trip whose date range contains this receipt's date
  const suggested = (!rec.trip_id && rec.date)
    ? trips.find((t) => t.start_date && t.end_date && rec.date >= t.start_date && rec.date <= t.end_date)
    : null;

  const assignSuggested = async (t) => {
    setSaving(true);
    try {
      await api.patch(`/receipts/${rec.id}/meta`, { trip_id: t.id });
      setTripId(t.id);
      toast.success(`Assigned to "${t.name}"`);
      onSaved && onSaved();
    } catch { toast.error("Could not assign"); }
    finally { setSaving(false); }
  };

  return (
    <Card data-testid="business-expense-card">
      <div className="flex items-center gap-2 mb-3">
        {isBiz ? <Briefcase size={18} weight="duotone" className="text-riq-primary" /> : <Airplane size={18} weight="duotone" className="text-riq-primary" />}
        <h3 className="font-head font-semibold text-riq-primary">{isBiz ? "Business expense" : "Trip / holiday"}</h3>
      </div>
      {suggested && !tripId && (
        <div data-testid="trip-suggestion" className="mb-4 rounded-xl border border-riq-gold/40 bg-riq-gold/10 p-3">
          <div className="flex items-start gap-2">
            <Sparkle size={16} weight="fill" className="text-riq-gold mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-slate-800">This looks like it's from your <span className="font-semibold">"{suggested.name}"</span> trip.</p>
              <p className="text-xs text-muted-foreground mt-0.5">{rec.date} falls within {suggested.start_date} → {suggested.end_date}.</p>
              <button data-testid="trip-suggestion-assign" onClick={() => assignSuggested(suggested)} disabled={saving} className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-riq-primary text-white px-3.5 py-1.5 text-xs font-semibold disabled:opacity-60"><Check size={13} weight="bold" /> Assign to this trip</button>
            </div>
          </div>
        </div>
      )}
      {isBiz && (
        <label className="block mb-3">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Description</span>
          <input data-testid="expense-description" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="e.g. Client lunch, taxi to airport" className="riq-input w-full mt-1" />
        </label>
      )}
      <label className="block mb-4">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Assign to trip</span>
        <select data-testid="expense-trip" value={tripId} onChange={(e) => setTripId(e.target.value)} className="mt-1 w-full bg-secondary/60 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-riq-primary/20">
          <option value="">{isBiz ? "— No trip (daily expense) —" : "— No trip —"}</option>
          {trips.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        {trips.length === 0 && <p className="mt-1.5 text-xs text-muted-foreground">Create a trip on the Trips page first (e.g. "December holiday").</p>}
      </label>
      <button data-testid="expense-save-btn" onClick={save} disabled={saving} className="flex items-center gap-2 rounded-full bg-riq-primary text-white px-5 py-2.5 text-sm font-medium disabled:opacity-60">{saving ? "Saving…" : isBiz ? "Save expense details" : "Save trip details"}</button>
    </Card>
  );
}


function Receiptish() { return <span className="text-riq-gold text-xl">🧾</span>; }
function Row({ label, value, bold }) { return <div className="flex justify-between"><span className="text-muted-foreground">{label}</span><span className={bold ? "font-head font-bold text-base" : ""}>{value}</span></div>; }
function WarrantyRow({ w, onChange, onRemove }) {
  const [editing, setEditing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const s = w.status;
  const tone = s === "expired" ? { bg: "bg-[#FFECEC]", fg: "text-[#FF6B6B]", Icon: WarningCircle }
    : s === "expiring_soon" ? { bg: "bg-riq-gold/25", fg: "text-[#B25E00]", Icon: Clock }
    : { bg: "bg-[#E4F7ED]", fg: "text-[#22B573]", Icon: CheckCircle };
  const statusLabel = s === "expired" ? "Expired" : s === "expiring_soon" ? `Expires in ${Math.max(w.days_until, 0)} days` : `${w.days_until} days left`;
  const claimPack = async () => {
    setDownloading(true);
    try {
      await downloadFile(`/warranties/${w.id}/claim-pack`, `claim_pack_${(w.label || "warranty").replace(/[^A-Za-z0-9]+/g, "_")}.pdf`);
      toast.success("Claim pack downloaded");
    } catch { toast.error("Could not generate the claim pack"); }
    finally { setDownloading(false); }
  };
  return (
    <div data-testid={`warranty-row-${w.id}`} className="rounded-xl border border-riq-border p-3.5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="font-medium text-sm truncate">{w.item_name || "Whole receipt"}</div>
          <div className="text-xs text-muted-foreground mt-0.5">{periodLabel(w.period_months)} cover · expires {longDate(w.end_date)}</div>
        </div>
        <span className={`rounded-full ${tone.bg} ${tone.fg} px-3 py-1.5 text-xs font-semibold flex items-center gap-1 shrink-0`}><tone.Icon size={13} weight="fill" /> {statusLabel}</span>
      </div>
      {editing && (
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`warranty-row-period-${w.id}`}>
          {WARRANTY_PERIODS.map((p) => (
            <button key={p.m} onClick={() => { onChange(w.id, p.m); setEditing(false); }} className={`rounded-full px-3 py-1.5 text-xs font-medium ${p.m === w.period_months ? "bg-riq-primary text-white" : "bg-secondary text-neutral-600 hover:bg-secondary/70"}`}>{p.l}</button>
          ))}
        </div>
      )}
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <button data-testid={`warranty-row-claim-${w.id}`} onClick={claimPack} disabled={downloading} className="flex items-center gap-1.5 rounded-full bg-riq-primary text-white px-3 py-1.5 text-xs disabled:opacity-60">{downloading ? <CircleNotch size={14} className="animate-spin" /> : <FilePdf size={14} weight="fill" />} Claim pack</button>
        <button data-testid={`warranty-row-edit-${w.id}`} onClick={() => setEditing((v) => !v)} className="flex items-center gap-1.5 rounded-full border border-riq-border px-3 py-1.5 text-xs hover:border-riq-primary"><Clock size={14} weight="duotone" /> Change period</button>
        <button data-testid={`warranty-row-remove-${w.id}`} onClick={() => onRemove(w.id)} className="flex items-center gap-1.5 rounded-full border border-riq-border px-3 py-1.5 text-xs text-destructive hover:border-destructive ml-auto"><Trash size={14} /> Remove</button>
      </div>
    </div>
  );
}

function WarrantyBlock({ warranties, items, onAdd, onChange, onRemove }) {
  const [months, setMonths] = useState(12);
  const [itemId, setItemId] = useState("");
  const list = warranties || [];
  const covered = new Set(list.map((w) => w.item_id).filter(Boolean));
  const wholeCovered = list.some((w) => !w.item_id);
  return (
    <Card data-testid="warranty-block">
      <div className="flex items-center gap-2 mb-3"><SealCheck size={18} weight="duotone" className="text-riq-primary" /><h3 className="font-head font-semibold text-riq-primary">Warranties</h3></div>
      {list.length > 0 && (
        <div className="space-y-2.5 mb-4" data-testid="warranty-block-list">
          {list.map((w) => <WarrantyRow key={w.id} w={w} onChange={onChange} onRemove={onRemove} />)}
        </div>
      )}
      <div className="rounded-xl border border-dashed border-riq-border p-4 bg-secondary/30">
        <div className="text-sm font-medium mb-2">Add a warranty</div>
        <label className="block mb-3">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">What is covered?</span>
          <select data-testid="warranty-item-select" value={itemId} onChange={(e) => setItemId(e.target.value)} className="mt-1 w-full bg-white rounded-xl border border-riq-border px-3 py-2.5 text-sm outline-none focus:border-riq-primary">
            <option value="">{wholeCovered ? "Whole receipt (already tracked — will update)" : "Whole receipt"}</option>
            {items.map((it) => {
              const name = it.canonical_name || it.description || "Item";
              return <option key={it.id} value={it.id}>{covered.has(it.id) ? `${name} (already tracked)` : name}</option>;
            })}
          </select>
        </label>
        <div className="grid grid-cols-3 gap-2 mb-3" data-testid="warranty-block-period-picker">
          {WARRANTY_PERIODS.map((p) => (
            <button key={p.m} data-testid={`warranty-block-period-${p.m}`} onClick={() => setMonths(p.m)} className={`rounded-xl px-2 py-2 text-xs font-medium border transition-colors ${months === p.m ? "bg-riq-primary text-white border-riq-primary" : "bg-white text-neutral-600 border-riq-border hover:border-riq-primary"}`}>{p.l}</button>
          ))}
        </div>
        <button data-testid="warranty-block-add" onClick={() => onAdd(itemId, months)} className="w-full rounded-full bg-riq-primary text-white py-2.5 text-sm font-medium flex items-center justify-center gap-2"><SealCheck size={16} weight="fill" /> Add {periodLabel(months)} warranty</button>
      </div>
    </Card>
  );
}
function ConfBadge({ label, value }) {
  const color = value >= 90 ? "#42C97C" : value >= 70 ? "#FFAA00" : "#FF6B6B";
  return <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1.5 text-xs font-medium capitalize"><span className="w-2 h-2 rounded-full" style={{ background: color }} />{label} {value}%</span>;
}
function ItemConf({ pct }) {
  const t = pct >= 95 ? { c: "#1f7a48", b: "#E4F7ED" } : pct >= 85 ? { c: "#8a5a00", b: "#FFF1D6" } : { c: "#b3261e", b: "#FFE3E1" };
  return <span data-testid="item-conf-badge" style={{ color: t.c, background: t.b }} className="shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-bold">{Math.round(pct)}%</span>;
}
function EField({ label, value, onChange, type = "text", testId }) {
  return <label className="block"><span className="text-[10px] font-semibold uppercase tracking-wider text-white/60">{label}</span><input data-testid={testId} type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full bg-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none border border-white/20" /></label>;
}
function ESelect({ label, value, onChange, testId, options = CATEGORIES }) {
  const opts = value && !options.includes(value) ? [value, ...options] : options;
  return <label className="block"><span className="text-[10px] font-semibold uppercase tracking-wider text-white/60">{label}</span><select data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full bg-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none border border-white/20">{opts.map((c) => <option key={c} value={c} className="text-black">{c}</option>)}</select></label>;
}
